import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import { OrganizationType } from "../types/organization.type";
import { getSitesForOrg, resolveMarketNavPermission } from "../auth";
import type { MarketNavItem } from "../auth";
import { isReadOnlyAdmin } from "../utils/auth.helpers";

export function useAuthPermissions() {
  const {
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    user,
    canAccessPermission,
  } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const readOnly = useMemo(
    () => isReadOnlyAdmin(user, tokenPayload),
    [user, tokenPayload],
  );

  const can = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      canAccessPermission(required, legacyRoles, orgId),
    [canAccessPermission, orgId],
  );

  const canMutate = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      !readOnly && can(required, legacyRoles),
    [readOnly, can],
  );

  const canSeeMarketNavItem = useCallback(
    (item: MarketNavItem): boolean => {
      if (platformAdmin) {
        return true;
      }

      const perm = resolveMarketNavPermission(item.entity, item.action, orgId);
      if (!perm) {
        return false;
      }
      return can(perm, item.legacyRoles);
    },
    [platformAdmin, orgId, can],
  );

  const filterMarketNavItems = useCallback(
    (items: MarketNavItem[], orgType: OrganizationType): MarketNavItem[] => {
      return items
        .filter((item) => item.allowedTypes.includes(orgType))
        .filter((item) => canSeeMarketNavItem(item));
    },
    [canSeeMarketNavItem],
  );

  const sitesForOrg = useCallback(
    (targetOrgId: string) => getSitesForOrg(tokenPayload, targetOrgId),
    [tokenPayload],
  );

  return {
    orgId,
    user,
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    isReadOnly: readOnly,
    can,
    canMutate,
    canSeeMarketNavItem,
    filterMarketNavItems,
    sitesForOrg,
  };
}
